import useChartSort from '@/composables/chart-sort';
import useCustomBuckets from '@/composables/grid-page/chart/custom-buckets';
import useGoalCycles from '@/composables/goal-cycle/goal-cycle';
import useGoalProperty from '@/composables/property/goal-property';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import usePropertyOptions from '@/composables/property-option/property-option';
import useSpaces from '@/composables/space/spaces';
import useStatusProperty from '@/composables/goal/status-property';
import useUsers from '@/composables/user/users';
import { CHART_CATEGORY_LIMIT, CHART_SERIES_LIMIT } from '@/lib/constants';
import { UNASSIGNED_VALUE, cleanResult, edgeName, fillMissingCategories, getDayFromISOString, getDisplayValue, getMonthFromISOString, getQuarterFromISOString, getWeekFromISOString, getYearFromISOString, isDateGrouping, roundNumber } from '@/composables/grid-page/chart/utils';
import { aggregatorType, chartGroupByOption, gridPageChartColorScheme, gridPageChartType, propertyType } from 'shared/constants.json';
import { applyColorScheme, getColor, getSeriesColor } from '@/lib/charts/colors';
import { computed } from 'vue';
import { goal as goalConfig } from 'shared/api/query/configs.json';
import { textByLang } from 'shared/lib/language';
import { useI18n } from 'vue-i18n';

export default function useChartGrouping(chart, resultRaw) {
  const { t } = useI18n();
  const customBucket = computed(() => chart.value.groupBy.customBucket);
  const { findBucketLabel: findCustomBucketLabel, buckets: customBuckets } = useCustomBuckets(customBucket, resultRaw, computed(() => chart.value.groupBy));
  const stackedCustomBucket = computed(() => chart.value.aggregationConfig.stackOptions.customBucket);
  const { findBucketLabel: findStackedBucketLabel, buckets: stackedBuckets } = useCustomBuckets(stackedCustomBucket, resultRaw, computed(() => chart.value.aggregationConfig.stackOptions));
  const { userLang } = useLoggedInUser();
  const { selectSingle: selectGoalCycle, goalCycles } = useGoalCycles();
  const { selectSingle: selectPropertyOption } = usePropertyOptions();
  const { selectSingle: selectSpace, spaces } = useSpaces();
  const { selectSingle: selectUser, users } = useUsers();
  const { statusProperty } = useGoalProperty();
  const { leafOptions: leafStatuses } = useStatusProperty(statusProperty, userLang);

  const { sort, sortByConfig } = useChartSort();

  const truncateToLimit = (items, limit, groupBy) => {
    if (items.length <= limit) return items;
    if (isDateGrouping(groupBy)) {
      return items.slice(-limit);
    }
    return items.slice(0, limit);
  };

  const isStacked = computed(() => [gridPageChartType.line, gridPageChartType.bar, gridPageChartType.column, gridPageChartType.table].includes(chart.value.chartType)
      && chart.value.aggregationConfig.stackOptions.groupBy !== chartGroupByOption.none);

  const dateGroupingFunctions = {
    [chartGroupByOption.day]: getDayFromISOString,
    [chartGroupByOption.week]: getWeekFromISOString,
    [chartGroupByOption.month]: getMonthFromISOString,
    [chartGroupByOption.quarter]: getQuarterFromISOString,
    [chartGroupByOption.year]: getYearFromISOString,
  };

  // All aggregators mutate `acc` in place. Keeps the hot reduce loop
  // allocation-free per item (M items × N groups × K stacks on large dashboards).
  const aggregationFn = {
    [aggregatorType.count]: (acc, item) => {
      acc.count += item.count;
      return acc;
    },
    [aggregatorType.sum]: (acc, item) => {
      acc.sum += item.sum;
      return acc;
    },
    [aggregatorType.avg]: (acc, item) => {
      const total = (acc.avg * acc.count) + (item.avg * item.count);
      const count = acc.count + item.count;
      acc.avg = count === 0 ? 0 : total / count;
      acc.count = count;
      return acc;
    },
    [aggregatorType.min]: (acc, item) => {
      if (acc.min === null || item.min < acc.min) {
        acc.min = item.min;
      }
      return acc;
    },
    [aggregatorType.max]: (acc, item) => {
      if (acc.max === null || item.max > acc.max) {
        acc.max = item.max;
      }
      return acc;
    },
    [aggregatorType.countDistinct]: (acc, item) => {
      if (acc.values[item.subGroup] === undefined) {
        acc[aggregatorType.countDistinct] += 1;
        acc.values[item.subGroup] = {};
      }
      return acc;
    },
  };

  const totalAggregator = {
    [aggregatorType.count]: aggregationFn[aggregatorType.count],
    [aggregatorType.sum]: aggregationFn[aggregatorType.sum],
    [aggregatorType.avg]: aggregationFn[aggregatorType.avg],
    [aggregatorType.min]: aggregationFn[aggregatorType.min],
    [aggregatorType.max]: aggregationFn[aggregatorType.max],
    [aggregatorType.countDistinct]: (acc, item) => {
      Object.assign(acc.values, item.values);
      acc[aggregatorType.countDistinct] = Object.keys(acc.values).length;
      return acc;
    },
  };

  // Factories returning a fresh accumulator. Replaces a previous
  // `copy(initialValue[aggregation])` (JSON.parse(JSON.stringify(...))) that
  // was a meaningful chunk of GC pressure on high-cardinality dashboards.
  const initialValue = {
    [aggregatorType.count]: () => ({ count: 0 }),
    [aggregatorType.sum]: () => ({ sum: 0 }),
    [aggregatorType.avg]: () => ({ avg: 0, count: 0 }),
    [aggregatorType.min]: () => ({ min: Infinity }),
    [aggregatorType.max]: () => ({ max: -Infinity }),
    [aggregatorType.countDistinct]: () => ({ [aggregatorType.countDistinct]: 0, values: {} }),
  };

  const getGroupEntity = (groupBy, groupValue) => {
    if (groupBy.groupBy === chartGroupByOption.none) {
      return undefined;
    }

    if (groupBy.isProperty === false) {
      if (groupBy.edge === goalConfig.edges.goalCycle) {
        return selectGoalCycle(groupValue);
      }
      if (groupBy.edgeType === propertyType.user) {
        return selectUser(groupValue);
      }
      if (groupBy.edgeType === propertyType.space) {
        return selectSpace(groupValue);
      }
      return undefined;
    }

    if (groupBy.property?.type === propertyType.space) {
      return selectSpace(groupValue);
    }

    if (groupBy.property?.type === propertyType.user) {
      return selectUser(groupValue);
    }

    if (groupBy.property?.options) {
      return selectPropertyOption(groupValue);
    }

    return undefined;
  };

  const getGroups = (result, groupBy, valueKey, isStackOptions = false) => {
    const groupMap = new Map();
    result.forEach((item) => {
      const group = groupByMap(groupBy.groupBy, isStackOptions)(item[valueKey], userLang.value);
      if (group === null || group === undefined) {
        return;
      }
      if (!groupMap.has(group)) {
        groupMap.set(group, true);
      }
    });

    return Array.from(groupMap.keys()).map((g) => {
      const entity = getGroupEntity(groupBy, g);
      const category = {
        title: getDisplayValue(g, entity, groupBy, userLang.value),
        originalValue: g,
        color: getColor(entity?.color),
        ...(entity !== undefined && entity !== null && { hasEntityColor: true }),
      };
      if (groupBy.groupBy === chartGroupByOption.bucket) {
        const bucketList = isStackOptions ? stackedBuckets : customBuckets;
        const bucketIndex = bucketList.value.findIndex((b) => b.label === g);
        if (bucketIndex !== -1) {
          const bucket = bucketList.value[bucketIndex];
          category.bucketMin = bucket.min;
          category.bucketMax = bucket.max;
          category.bucketIndex = bucketIndex;
        }
      }
      return category;
    });
  };

  const groupByMap = (groupBy, isStackOptions = false) => {
    if (isDateGrouping(groupBy)) {
      return dateGroupingFunctions[groupBy];
    }
    if (groupBy === chartGroupByOption.bucket) {
      if (isStackOptions) {
        return findStackedBucketLabel;
      }
      return findCustomBucketLabel;
    }
    return (val) => val;
  };

  const groupResult = (result, aggregation, group, subGroup = null) => {
    const lang = userLang.value;
    const mainGroupFn = groupByMap(group.groupBy);
    const subGroupFn = subGroup !== null ? groupByMap(subGroup.groupBy, true) : null;
    const aggFn = aggregationFn[aggregation];
    const initFn = initialValue[aggregation];
    const mainValueKey = group.valueKey;
    const subValueKey = subGroup !== null ? subGroup.valueKey : null;
    const acc = {};
    for (let i = 0; i < result.length; i += 1) {
      const item = result[i];
      const groupKey = mainGroupFn(item[mainValueKey], lang);
      if (groupKey === null || groupKey === undefined) {
        continue;
      }
      let entry = acc[groupKey];
      if (!entry) {
        entry = { groups: {}, ...initFn() };
        acc[groupKey] = entry;
      }
      aggFn(entry, item);
      if (subGroupFn === null) {
        continue;
      }
      const subGroupKey = subGroupFn(item[subValueKey], lang);
      if (subGroupKey === null || subGroupKey === undefined) {
        continue;
      }
      let subEntry = entry.groups[subGroupKey];
      if (!subEntry) {
        subEntry = initFn();
        entry.groups[subGroupKey] = subEntry;
      }
      aggFn(subEntry, item);
    }
    return acc;
  };

  const shouldShowUnassigned = (excludeNullValues, unassignedValue) => {
    if (unassignedValue === null) return false;
    if (Array.isArray(unassignedValue)) return !excludeNullValues || unassignedValue.length > 0;
    return !(excludeNullValues && unassignedValue === 0);
  };

  const unassignedCategory = (groupBy) => {
    let title = t('gridPageChartEditor.unassignedValue');
    if (groupBy) {
      const hasLabel = groupBy.isProperty
        ? groupBy.property?.label && textByLang(groupBy.property.label, userLang.value)
        : groupBy.edge;
      if (hasLabel) {
        const name = edgeName(groupBy, t, userLang.value);
        if (name) {
          title = t('gridPageChartEditor.unassignedEdge', { edge: name });
        }
      }
    }
    return { title, originalValue: UNASSIGNED_VALUE, color: getColor(null) };
  };

  const addToGroupedRes = (groupedRes, aggregator, categoryKey, stackKey, item) => {
    const aggFn = aggregationFn[aggregator];
    const initFn = initialValue[aggregator];
    let category = groupedRes[categoryKey];
    if (!category) {
      category = { groups: {}, ...initFn() };
      groupedRes[categoryKey] = category;
    }
    let stack = category.groups[stackKey];
    if (!stack) {
      stack = initFn();
      category.groups[stackKey] = stack;
    }
    aggFn(stack, item);
    aggFn(category, item);
  };

  const processChartData = (result, unassignedValue = null, noStackData = []) => {
    const aggregator = chart.value.aggregationConfig.aggregation.aggregator;
    const excludeNullValues = chart.value.excludeNullValues ?? false;
    const fillContext = {
      goalCycles: goalCycles.value,
      spaces: spaces.value,
      users: users.value,
      leafStatuses: leafStatuses.value,
      getColor,
    };

    const filteredResult = cleanResult(
      result,
      chart.value.groupBy,
      chart.value.aggregationConfig.stackOptions,
      userLang.value,
      fillContext,
    );

    const showUnassigned = shouldShowUnassigned(excludeNullValues, unassignedValue);

    if (isStacked.value) {
      const stackedGroups = getGroups(filteredResult, chart.value.aggregationConfig.stackOptions, 'stackGroup', true);
      const groups = getGroups(filteredResult, chart.value.groupBy, 'val');
      const groupedRes = groupResult(
        filteredResult,
        aggregator,
        { groupBy: chart.value.groupBy.groupBy, valueKey: 'val' },
        { groupBy: chart.value.aggregationConfig.stackOptions.groupBy, valueKey: 'stackGroup' },
      );

      // Items with main groupBy attr but WITHOUT stack attr → "No value" stack per category
      let hasNoStackInStack = false;
      if (noStackData.length > 0) {
        const resolvedNoStack = cleanResult(
          noStackData,
          chart.value.groupBy,
          { groupBy: chartGroupByOption.none },
          userLang.value,
          fillContext,
        );
        resolvedNoStack.forEach((item) => {
          addToGroupedRes(groupedRes, aggregator, item.val, UNASSIGNED_VALUE, item);
        });
        hasNoStackInStack = true;
      }

      // Build unassigned entry with per-stack groups from raw @groupby entries
      if (showUnassigned && Array.isArray(unassignedValue)) {
        // Separate "no stack" entries (items missing both attrs) from regular stack entries
        const regularEntries = unassignedValue.filter((item) => item.stackGroup !== UNASSIGNED_VALUE);
        const noStackEntries = unassignedValue.filter((item) => item.stackGroup === UNASSIGNED_VALUE);

        // Resolve regular stack entries via cleanResult (handles hex UIDs, ___UID___, etc.)
        const remapped = regularEntries.map((item) => ({ ...item, val: item.stackGroup }));
        const resolvedUnassigned = cleanResult(
          remapped,
          chart.value.aggregationConfig.stackOptions,
          { groupBy: chartGroupByOption.none },
          userLang.value,
          fillContext,
        );
        resolvedUnassigned.forEach((item) => {
          addToGroupedRes(groupedRes, aggregator, UNASSIGNED_VALUE, item.val, item);
        });

        // Add "no stack" items to the UNASSIGNED_VALUE stack group
        noStackEntries.forEach((item) => {
          addToGroupedRes(groupedRes, aggregator, UNASSIGNED_VALUE, UNASSIGNED_VALUE, item);
        });

        if (noStackEntries.length > 0) {
          hasNoStackInStack = true;
        }
      }

      const aggregatedTotal = Object.values(groupedRes).reduce(
        totalAggregator[aggregator],
        initialValue[aggregator](),
      );
      const sortedGroups = sort(groups, chart.value.groupBy);
      const filledCategories = fillMissingCategories(sortedGroups, chart.value.groupBy, userLang.value, excludeNullValues, { ...fillContext, buckets: customBuckets.value });
      if (showUnassigned) {
        filledCategories.push(unassignedCategory(chart.value.groupBy));
      }
      const allCategories = sortByConfig(filledCategories, chart.value.groupBy, groupedRes, aggregator);
      let categories = allCategories;
      const xHidden = new Set(chart.value.groupBy.hiddenGroups);
      if (xHidden.size > 0) {
        categories = categories.filter((g) => !xHidden.has(String(g.originalValue)));
      }
      const sortedStackedGroups = sort(stackedGroups, chart.value.aggregationConfig.stackOptions);
      const filledStackedGroups = fillMissingCategories(sortedStackedGroups, chart.value.aggregationConfig.stackOptions, userLang.value, excludeNullValues, { ...fillContext, buckets: stackedBuckets.value });
      // Add "No value" stack group AFTER fillMissingCategories (which replaces the list for enumerable types)
      if (hasNoStackInStack && !filledStackedGroups.some((g) => g.originalValue === UNASSIGNED_VALUE)) {
        filledStackedGroups.push(unassignedCategory(chart.value.aggregationConfig.stackOptions));
      }
      const allStackedGroups = sortByConfig(filledStackedGroups, chart.value.aggregationConfig.stackOptions);
      let sortedFilledStackedGroups = allStackedGroups;
      const stackHidden = new Set(chart.value.aggregationConfig.stackOptions.hiddenGroups);
      if (stackHidden.size > 0) {
        sortedFilledStackedGroups = sortedFilledStackedGroups.filter((g) => !stackHidden.has(String(g.originalValue)));
      }

      let total = roundNumber(aggregatedTotal[aggregator]);
      if (showUnassigned && typeof unassignedValue === 'number') {
        total = roundNumber(total + unassignedValue);
      }

      const totalCategoryCount = categories.length;
      const totalSeriesCount = sortedFilledStackedGroups.length;
      const truncatedCategories = truncateToLimit(categories, CHART_CATEGORY_LIMIT, chart.value.groupBy.groupBy);
      const truncatedStackedGroups = truncateToLimit(sortedFilledStackedGroups, CHART_SERIES_LIMIT, chart.value.aggregationConfig.stackOptions.groupBy);
      const isTruncated = truncatedCategories.length < totalCategoryCount || truncatedStackedGroups.length < totalSeriesCount;

      const coloredStackedGroups = applyColorScheme(chart.value.colorScheme, truncatedStackedGroups, { colorUnassigned: true });
      return {
        categories: truncatedCategories.map((category) => category.title),
        filteredCategories: categories,
        allCategories,
        filteredStackedGroups: sortedFilledStackedGroups,
        allStackedGroups,
        series: coloredStackedGroups.map((stackGroup) => ({
          name: stackGroup.title,
          color: stackGroup.color,
          data: truncatedCategories.map((category) => {
            const value = groupedRes[category.originalValue]?.groups[stackGroup.originalValue]?.[aggregator];
            return { y: value !== undefined ? roundNumber(value) : 0 };
          }),
        })),
        total,
        isTruncated,
        ...(isTruncated && {
          shownCount: truncatedCategories.length,
          totalCount: totalCategoryCount,
          shownSeriesCount: truncatedStackedGroups.length,
          totalSeriesCount,
        }),
      };
    }
    const groupedResult = groupResult(
      filteredResult,
      aggregator,
      { groupBy: chart.value.groupBy.groupBy, valueKey: 'val' },
    );
    const aggregatedTotal = Object.values(groupedResult).reduce(
      totalAggregator[aggregator],
      initialValue[aggregator](),
    );
    let total = roundNumber(aggregatedTotal[aggregator]);
    if (showUnassigned) {
      total = roundNumber(total + unassignedValue);
    }

    const groups = getGroups(filteredResult, chart.value.groupBy, 'val');
    const sortedGroups = sort(groups, chart.value.groupBy);
    const filledCategories = fillMissingCategories(sortedGroups, chart.value.groupBy, userLang.value, excludeNullValues, { ...fillContext, buckets: customBuckets.value });
    if (showUnassigned) {
      filledCategories.push(unassignedCategory(chart.value.groupBy));
    }
    const allCategories = sortByConfig(filledCategories, chart.value.groupBy, groupedResult, aggregator);
    let categories = allCategories;
    const xHiddenNonStacked = new Set(chart.value.groupBy.hiddenGroups);
    if (xHiddenNonStacked.size > 0) {
      categories = categories.filter((g) => !xHiddenNonStacked.has(String(g.originalValue)));
    }

    const totalCategoryCount = categories.length;
    const truncatedCategories = truncateToLimit(categories, CHART_CATEGORY_LIMIT, chart.value.groupBy.groupBy);
    const isTruncated = truncatedCategories.length < totalCategoryCount;

    const isLine = chart.value.chartType === gridPageChartType.line;
    const isPie = chart.value.chartType === gridPageChartType.pie;
    const scheme = chart.value.colorScheme;
    const isMonochromatic = scheme && scheme !== gridPageChartColorScheme.automatic && scheme !== gridPageChartColorScheme.colorful;
    const useSingleColor = !isLine && !isPie && isMonochromatic;
    const coloredCategories = applyColorScheme(scheme, truncatedCategories);
    const singleColor = useSingleColor ? coloredCategories[0]?.color : null;
    const seriesData = coloredCategories.map((category) => {
      let y = 0;
      if (category.originalValue === UNASSIGNED_VALUE) {
        y = roundNumber(unassignedValue);
      } else if (groupedResult[category.originalValue]) {
        y = roundNumber(groupedResult[category.originalValue][aggregator]);
      }
      const color = useSingleColor ? singleColor : category.color;
      return { name: category.title, y, ...(!isLine && { color }) };
    });
    const series = [{ data: seriesData }];
    if (isLine) {
      series[0].color = getSeriesColor(scheme);
    }
    return {
      categories: coloredCategories.map((category) => category.title),
      filteredCategories: coloredCategories,
      allCategories,
      series,
      total,
      isTruncated,
      ...(isTruncated && {
        shownCount: truncatedCategories.length,
        totalCount: totalCategoryCount,
      }),
    };
  };

  return { processChartData };
}
